import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface BlackforestlabsFlux11ProParams {
  /**
   * 文本提示词，描述要生成的图像内容
   * @default ""
   */
  prompt: string;
  /**
   * 图像提示，必须是有效的Base64编码图像
   */
  image_prompt?: string;
  /**
   * 图像宽度，必须是32的倍数，范围256-1440像素
   * @default 1024
   * @range 256 - 1440
   */
  width?: number;
  /**
   * 图像高度，必须是32的倍数，范围256-1440像素
   * @default 1024
   * @range 256 - 1440
   */
  height?: number;
  /**
   * 是否启用提示词增强
   * @default false
   */
  prompt_upsampling?: boolean;
  /**
   * 随机种子，用于可重现的生成结果
   */
  seed?: number;
  /**
   * 安全容忍度，范围0-6
   * @default 2
   * @range 0 - 6
   */
  safety_tolerance?: number;
  /**
   * 输出格式
   * @default "jpeg"
   * @values jpeg | png
   */
  output_format?: 'jpeg' | 'png';
}

export interface BlackforestlabsFlux11ProResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * blackforestlabs_flux_11_pro
 *
 * Black Forest Labs FLUX 1.1 [pro] 图像生成实现
 * 
 * Black Forest Labs FLUX 1.1 [pro] image generation implementation.
 * 
 * Model: flux-pro-1.1
 * Features: 文字生成图像 (Text-to-Image)
 * Resolution: 256-1440px (宽高必须是32的倍数)
 * Image Prompt: 支持图像提示
 * 
 * API Documentation: https://docs.bfl.ai/api-reference/tasks/generate-an-image-with-flux-11-[pro]
 *
 * @example
 * ```typescript
 * import { initSeacloud, blackforestlabsFlux11Pro } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await blackforestlabsFlux11Pro({
 *   // 参数见 BlackforestlabsFlux11ProParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 BlackforestlabsFlux11ProParams 接口定义
 * @returns 生成的资源列表
 */
export async function blackforestlabsFlux11Pro(
  params: BlackforestlabsFlux11ProParams,
): Promise<BlackforestlabsFlux11ProResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'blackforestlabs_flux_11_pro',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: BlackforestlabsFlux11ProResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
